import { z } from 'zod';
import { adapterClient, handleApiError } from './client';
import { canonicalProjectId } from './projectViews';
import { entityIdSchema } from '@/types/current-project';

export const submissionDetailsSchema = z
  .object({
    documentation: z.string().nullable(),
    links: z.string().nullable(),
  })
  .strict()
  .superRefine((details, context) => {
    for (const field of ['documentation', 'links'] as const) {
      if (
        details[field] !== null &&
        new TextEncoder().encode(details[field]).length > 2048
      )
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Use at most 2048 UTF-8 bytes, or leave this field empty.',
        });
    }
  });
export type SubmissionDetails = z.infer<typeof submissionDetailsSchema>;
const presentationSchema = z.object({
  submissionId: entityIdSchema,
  revision: z.literal(1),
  documentation: z.string().nullable(),
  links: z.string().nullable(),
});
const path = (submissionId: string) =>
  '/completion-submissions/' +
  canonicalProjectId(submissionId) +
  '/presentation';
export async function getSubmissionPresentation(
  submissionId: string,
  signal?: AbortSignal
) {
  try {
    const { data } = await adapterClient.get<unknown>(path(submissionId), {
      signal,
    });
    return presentationSchema.nullable().parse(data);
  } catch (error) {
    handleApiError(error, 'load milestone submission details');
  }
}
export async function putSubmissionPresentation(
  submissionId: string,
  details: SubmissionDetails
) {
  try {
    const { data } = await adapterClient.put<unknown>(path(submissionId), {
      expectedRevision: 0,
      ...submissionDetailsSchema.parse(details),
    });
    return presentationSchema.parse(data);
  } catch (error) {
    handleApiError(error, 'save milestone submission details');
  }
}
